'use client';

import { useEffect, useRef, useState } from 'react';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import {
  Spinner,
  toasterApi,
  DrawerCustomSection,
  Text,
  Button,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import type { SignalDto } from '@terra/shared-contracts-signals';
import {
  useInternalNetworkCrownJewels,
  useInternalNetworkScenarios,
  useStopInternalNetworkScenario,
} from '../../../hooks/api/useInternalNetworkScenarios';
import { resolveScenarioAttackPathCenterNodeId } from './resolve-scenario-attack-path-center-node-id';
import { InternalNetworkScenarioCard } from './InternalNetworkScenarioCard';

type InternalNetworkSignalScenariosSectionProps = {
  signalId: string | null;
  networkId: string;
  signal: SignalDto | undefined;
  highlightedScenarioId?: string | null;
  enabled?: boolean;
  variant?: 'drawer' | 'tab';
};

export function InternalNetworkSignalScenariosSection({
  signalId,
  networkId,
  signal,
  highlightedScenarioId = null,
  enabled = true,
  variant = 'drawer',
}: InternalNetworkSignalScenariosSectionProps) {
  const t = useTranslations('internalNetwork.scenarios');
  const tCommon = useTranslations('common');
  const { scenarios, isLoading, isError, refetch } =
    useInternalNetworkScenarios({
      signalId,
      enabled,
    });
  const { crownJewels } = useInternalNetworkCrownJewels({
    networkId,
    enabled,
  });
  const stopScenario = useStopInternalNetworkScenario({ signalId, networkId });
  const [pendingStopScenarioId, setPendingStopScenarioId] = useState<
    string | null
  >(null);
  const [expandedGraphScenarioId, setExpandedGraphScenarioId] = useState<
    string | null
  >(null);
  const scenarioPanelRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!highlightedScenarioId) {
      return;
    }

    const panel = scenarioPanelRefs.current[highlightedScenarioId];

    panel?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [highlightedScenarioId, scenarios]);

  const handleStop = (scenarioId: string) => {
    setPendingStopScenarioId(scenarioId);
    stopScenario.mutate(scenarioId, {
      onSuccess: async () => {
        await refetch();
      },
      onError: () => {
        toasterApi.error({ title: tCommon('error') });
      },
      onSettled: () => {
        setPendingStopScenarioId(null);
      },
    });
  };

  if (!signalId || !enabled) {
    return null;
  }

  if (isLoading) {
    if (variant === 'tab') {
      return (
        <HStack justifyContent="center" py={8}>
          <Spinner size="sm" />
        </HStack>
      );
    }

    return (
      <DrawerCustomSection title={t('title')} variant="box">
        <HStack justifyContent="center" py={4}>
          <Spinner size="sm" />
        </HStack>
      </DrawerCustomSection>
    );
  }

  if (isError) {
    const errorContent = (
      <VStack gap={3} alignItems="center" py={variant === 'tab' ? 8 : 4}>
        <Text fontSize="sm" color="fg.error">
          {tCommon('error')}
        </Text>
        <Button size="sm" variant="outline" onClick={() => void refetch()}>
          {tCommon('retry')}
        </Button>
      </VStack>
    );

    if (variant === 'tab') {
      return errorContent;
    }

    return (
      <DrawerCustomSection title={t('title')} variant="box">
        {errorContent}
      </DrawerCustomSection>
    );
  }

  if (scenarios.length === 0) {
    if (variant === 'tab') {
      return (
        <Box py={8}>
          <Text fontSize="sm" color="fg.muted">
            {t('empty')}
          </Text>
        </Box>
      );
    }

    return null;
  }

  const scenarioCards = (
    <VStack gap={4} alignItems="stretch" w="full">
      {scenarios.map((scenario, index) => {
        const canStop = scenario.status === 'RUNNING';
        const attackPathCenterNodeId =
          signal !== undefined
            ? resolveScenarioAttackPathCenterNodeId({
                scenario,
                signal,
                crownJewels: crownJewels ?? [],
              })
            : null;

        return (
          <InternalNetworkScenarioCard
            key={scenario.id}
            scenario={scenario}
            networkId={networkId}
            signal={signal}
            attackPathCenterNodeId={attackPathCenterNodeId}
            isHighlighted={highlightedScenarioId === scenario.id}
            canStop={canStop}
            isStopPending={pendingStopScenarioId === scenario.id}
            isAttackPathExpanded={expandedGraphScenarioId === scenario.id}
            onStop={() => handleStop(scenario.id)}
            onToggleAttackPath={() =>
              setExpandedGraphScenarioId((current) =>
                current === scenario.id ? null : scenario.id,
              )
            }
            panelRef={(element) => {
              scenarioPanelRefs.current[scenario.id] = element;
            }}
            crownJewels={crownJewels ?? []}
            showDivider={index > 0}
          />
        );
      })}
    </VStack>
  );

  if (variant === 'tab') {
    return scenarioCards;
  }

  return (
    <DrawerCustomSection title={t('title')} variant="box">
      {scenarioCards}
    </DrawerCustomSection>
  );
}
